import { Button, Checkbox, Chip } from "@heroui/react";
import EditableChip from "@/components/common/EditableChip";
import HintTooltip from "@/components/common/HintTooltip";
import { createColumnHelper, type ColumnDef } from "@tanstack/react-table";
import { Trash2 } from "lucide-react";

import RowDragHandle from "@/components/table/RowDragHandle";
import { formatAmount } from "@/lib/format";
import InlineEditCell from "@/components/common/InlineEditCell";
import "@/components/table/columnMeta";
import type { ProjectItem, ProjectItemInput } from "./api";

/**
 * 案件明細テーブルの列定義。
 *
 * 見積明細と同じ作りだが、**金額が無く納期がある**。編集の仕方も同じ考え方で
 * 分けている:
 *
 * - **納期・出荷予定日・発送日・摘要** … セルの中で直接編集する
 *   （互いに影響しない）
 * - **図番・品名・材質・表面処理・数量** … これもセルの中で直接編集するが、
 *   **見積から起こした行では直せない**。案件は見積で約束した内容で動くので、
 *   案件の側で書き換えると客先に出した見積と現場が作るものが食い違う
 *   （API も 409 で断る。[[_reject_quotation_owned_changes]]）。直すときは見積の
 *   側で直すか、見積との紐付けを外す
 *
 * **図面 PDF はこの表から扱わない**。版の一覧も絵の表示も図面タブ
 * （[[ProjectDrawingsPanel]]）に揃っていて、あちらは絵で探せる。表に小さな入口を
 * 2 つ並べても、どちらが何を開くのかが読めないだけだった。
 */
export interface ProjectItemColumnHandlers {
  canWrite: boolean;
  /** 先頭・末尾で上下ボタンを無効にするための総行数。 */
  itemCount: number;
  /** まとめて削除する対象として選ばれている明細の id。 */
  selectedIds: Set<number>;
  /** 行の選択を切り替える。 */
  onToggleSelect: (item: ProjectItem) => void;
  onSaveField: (item: ProjectItem, input: ProjectItemInput) => Promise<string | null>;
  /** 「見積」の列を押したとき。見積明細を選んで結び付けるダイアログを開く。 */
  onOpenQuotationLink: (item: ProjectItem) => void;
  onMove: (item: ProjectItem, direction: -1 | 1) => void;
  onDelete: (item: ProjectItem) => void;
}

const helper = createColumnHelper<ProjectItem>();

/**
 * その行の「何を作るか」を直せるか。**見積から起こした行は直せない**。
 *
 * 納期・出荷予定日・発送日・摘要はここに含めない（納期と出荷予定日は受注のときに
 * 案件側で決め、発送日は実際に出した日、摘要には現場の書き足しが入る）。
 */
function whatToMakeEditable(canWrite: boolean, item: ProjectItem) {
  return canWrite && item.quotation_item_id == null;
}

export function buildProjectItemColumns({
  canWrite,
  itemCount,
  selectedIds,
  onToggleSelect,
  onSaveField,
  onOpenQuotationLink,
  onMove,
  onDelete,
}: ProjectItemColumnHandlers): ColumnDef<ProjectItem, never>[] {
  return [
    // 選択列。ヘッダではなく表の上の「すべて選択」で全行を選ぶ（ヘッダのセルは
    // 並び替えのボタンで埋まっており、中に押せるものを置けない）。選ぶ目的が
    // 削除なので、押せない人には場所を取らせない。
    ...(canWrite
      ? [
          helper.display({
            id: "select",
            header: "選択",
            size: 60,
            enableResizing: false,
            meta: { label: "選択", mandatory: true, pinned: true },
            cell: (ctx) => {
              const item = ctx.row.original;
              return (
                <Checkbox
                  size="sm"
                  isSelected={selectedIds.has(item.project_item_id)}
                  onValueChange={() => onToggleSelect(item)}
                  aria-label={`${item.sequence} 行目を選択`}
                  classNames={{ base: "m-0 p-0", label: "hidden" }}
                />
              );
            },
          }),
        ]
      : []),
    helper.accessor("sequence", {
      id: "sequence",
      header: "行",
      size: 70,
      meta: { label: "行番号", mandatory: true, pinned: true },
      cell: (ctx) => {
        const row = ctx.row.original;
        return (
          <RowDragHandle
            rowId={row.project_item_id}
            label={`${row.sequence} 行目`}
            isDisabled={!canWrite}
            canMoveUp={row.sequence > 1}
            canMoveDown={row.sequence < itemCount}
            onMove={(direction) => onMove(row, direction)}
          >
            {row.sequence}
          </RowDragHandle>
        );
      },
    }),
    helper.accessor("drawing_number", {
      id: "drawing_number",
      header: "図番",
      size: 170,
      meta: { label: "図番" },
      cell: (ctx) => (
        <InlineEditCell
          label="図番"
          value={ctx.getValue()}
          canWrite={whatToMakeEditable(canWrite, ctx.row.original)}
          onSave={(v) =>
            onSaveField(ctx.row.original, { drawing_number: v ?? undefined })
          }
        />
      ),
    }),
    helper.accessor("item_name", {
      id: "item_name",
      header: "品名",
      size: 260,
      meta: { label: "品名" },
      cell: (ctx) => (
        <InlineEditCell
          label="品名"
          value={ctx.getValue()}
          canWrite={whatToMakeEditable(canWrite, ctx.row.original)}
          onSave={(v) => onSaveField(ctx.row.original, { item_name: v })}
        />
      ),
    }),
    helper.accessor("material", {
      id: "material",
      header: "材質",
      size: 150,
      meta: { label: "材質" },
      cell: (ctx) => (
        <InlineEditCell
          label="材質"
          value={ctx.getValue()}
          canWrite={whatToMakeEditable(canWrite, ctx.row.original)}
          onSave={(v) => onSaveField(ctx.row.original, { material: v })}
        />
      ),
    }),
    helper.accessor("surface_treatment", {
      id: "surface_treatment",
      header: "表面処理",
      size: 200,
      meta: { label: "表面処理" },
      cell: (ctx) => (
        <InlineEditCell
          label="表面処理"
          value={ctx.getValue()}
          canWrite={whatToMakeEditable(canWrite, ctx.row.original)}
          onSave={(v) => onSaveField(ctx.row.original, { surface_treatment: v })}
        />
      ),
    }),
    helper.accessor("quantity", {
      id: "quantity",
      header: "数量",
      size: 90,
      meta: { label: "数量", numeric: true },
      cell: (ctx) => (
        <InlineEditCell
          label="数量"
          type="number"
          numeric
          value={String(ctx.getValue())}
          canWrite={whatToMakeEditable(canWrite, ctx.row.original)}
          onSave={(v) => onSaveField(ctx.row.original, { quantity: Number(v ?? 0) })}
        />
      ),
    }),
    // 発注書から読み取った単価。**自社の見積ではない**ので「客先単価」と呼ぶ
    // （金額の根拠は見積側の工程にある）。読み違えを人が直せるよう編集可。
    helper.accessor("client_unit_price", {
      id: "client_unit_price",
      header: "客先単価",
      size: 110,
      meta: { label: "客先単価", numeric: true },
      cell: (ctx) => (
        <InlineEditCell
          label="客先単価"
          type="number"
          numeric
          value={ctx.getValue() ?? ""}
          // API は NUMERIC を文字列で返すので、表示だけ 3 桁区切りにする
          // （編集中は生の値のまま扱う）。
          displayValue={ctx.getValue() == null ? null : formatAmount(ctx.getValue()!)}
          canWrite={canWrite}
          onSave={(v) =>
            onSaveField(ctx.row.original, { client_unit_price: v || null })
          }
        />
      ),
    }),
    helper.accessor("client_unit_no", {
      id: "client_unit_no",
      header: "ユニットNo",
      size: 110,
      meta: { label: "客先のユニットNo" },
      // 客先の階層（京セラ）。**客先へ回答を返すときの行の鍵**なので直せる
      // ようにしておく（見積明細の同じ列と同じ扱い）。
      cell: (ctx) => (
        <InlineEditCell
          label="客先のユニットNo"
          value={ctx.getValue()}
          canWrite={canWrite}
          onSave={(v) => onSaveField(ctx.row.original, { client_unit_no: v })}
        />
      ),
    }),
    helper.accessor("client_bom_no", {
      id: "client_bom_no",
      header: "部品表No",
      size: 110,
      meta: { label: "客先の部品表No" },
      cell: (ctx) => (
        <InlineEditCell
          label="客先の部品表No"
          value={ctx.getValue()}
          canWrite={canWrite}
          onSave={(v) => onSaveField(ctx.row.original, { client_bom_no: v })}
        />
      ),
    }),
    helper.accessor("client_slip_number", {
      id: "client_slip_number",
      header: "伝票No",
      size: 130,
      meta: { label: "客先の伝票番号" },
      // 新東工業の紙にだけある、行ごとの伝票番号。請求書の「日付/伝番」欄に
      // 刷るので、直せるようにしてある（取り込みで入るが、紙の読み違いを
      // 直せないと請求書が出せなくなる）。
      cell: (ctx) => (
        <InlineEditCell
          label="客先の伝票番号"
          value={ctx.getValue()}
          canWrite={canWrite}
          onSave={(v) => onSaveField(ctx.row.original, { client_slip_number: v })}
        />
      ),
    }),
    helper.accessor("client_order_numbers", {
      id: "client_order_numbers",
      header: "客先購番",
      size: 200,
      meta: { label: "客先購番" },
      // この行に畳み込んだ購番。セット品は客先がセット 1 本ごとに購番を切るので
      // 複数本並び、**その本数がセット数**になる。**直せない**（紙の行そのものの
      // 記録で、取り込みで決まる値）。
      // 値の型は列定義全体で never に揃えているので、行から取り出す。
      cell: (ctx) => {
        const numbers = ctx.row.original.client_order_numbers;
        if (numbers.length === 0) return <span className="text-default-400">—</span>;
        return <span className="tabular-nums">{numbers.join(" / ")}</span>;
      },
    }),
    helper.accessor("deadline", {
      id: "deadline",
      header: "納期",
      size: 140,
      meta: { label: "納期" },
      // 明細ごとの納期。未設定なら案件の納期に従う（分納のときに行ごとに持つ）。
      cell: (ctx) => (
        <InlineEditCell
          label="納期"
          type="date"
          value={ctx.getValue()}
          canWrite={canWrite}
          onSave={(v) => onSaveField(ctx.row.original, { deadline: v })}
        />
      ),
    }),
    helper.accessor("machining_deadline", {
      id: "machining_deadline",
      header: "加工納期",
      size: 140,
      meta: { label: "加工納期" },
      // 社内で加工を上げる期限。客先への納期とは別で、**現場へ出す図面に刷るのは
      // こちら**（[[qr_stamp]]）。後工程のぶんを現場に引かせないために分けてある。
      cell: (ctx) => (
        <InlineEditCell
          label="加工納期"
          type="date"
          value={ctx.getValue()}
          canWrite={canWrite}
          onSave={(v) => onSaveField(ctx.row.original, { machining_deadline: v })}
        />
      ),
    }),
    helper.accessor("shipping_planned_date", {
      id: "shipping_planned_date",
      header: "出荷予定日",
      size: 140,
      meta: { label: "出荷予定日" },
      // 明細ごとの出荷予定日。未設定なら案件の出荷予定日に従う（受注のときは
      // 案件の値を写してくるので、分納のときだけ行ごとに直す）。
      cell: (ctx) => (
        <InlineEditCell
          label="出荷予定日"
          type="date"
          value={ctx.getValue()}
          canWrite={canWrite}
          onSave={(v) => onSaveField(ctx.row.original, { shipping_planned_date: v })}
        />
      ),
    }),
    helper.accessor("shipped_date", {
      id: "shipped_date",
      header: "発送日",
      size: 140,
      meta: { label: "発送日" },
      // 実際に出した日。空なら未発送。**案件の出荷済み（`shipped_at`）とは別**で、
      // 行ごとに出す分納はここで表す。案件の出荷ボタンはこの列を触らない。
      cell: (ctx) => (
        <InlineEditCell
          label="発送日"
          type="date"
          value={ctx.getValue()}
          canWrite={canWrite}
          onSave={(v) => onSaveField(ctx.row.original, { shipped_date: v })}
        />
      ),
    }),
    helper.accessor("quotation_item_id", {
      id: "quotation_item_id",
      header: "見積",
      size: 110,
      meta: { label: "見積との紐付け" },
      // 紐付いていない行は特急案件の見積後付け漏れかもしれないので目立たせる。
      // 押すと見積明細を選んで結び付けられる（[[ItemQuotationLinkDialog]]）。
      cell: (ctx) => {
        const item = ctx.row.original;
        const chip =
          ctx.getValue() != null ? (
            // この行の図番・品名・材質・表面処理・数量が直せない理由はここにある。
            // 鉛筆が消えた列を見ただけでは、権限の問題と見分けが付かない。
            <HintTooltip content="見積から起こした行です。図番・品名・材質・表面処理・数量は見積の側で直します。">
              <Chip size="sm" variant="flat" color="success">
                紐付済
              </Chip>
            </HintTooltip>
          ) : (
            <Chip size="sm" variant="flat" color="warning">
              未紐付
            </Chip>
          );
        return (
          <EditableChip
            label={`${item.sequence} 行目を見積明細に結び付け`}
            title="押すと見積明細を選んで結び付けられます"
            canWrite={canWrite}
            onPress={() => onOpenQuotationLink(item)}
          >
            {chip}
          </EditableChip>
        );
      },
    }),
    helper.accessor("note", {
      id: "note",
      header: "摘要",
      size: 220,
      meta: { label: "摘要" },
      cell: (ctx) => (
        <InlineEditCell
          label="摘要"
          value={ctx.getValue()}
          canWrite={canWrite}
          onSave={(v) => onSaveField(ctx.row.original, { note: v })}
        />
      ),
    }),
    helper.display({
      id: "actions",
      header: "操作",
      size: 70,
      enableResizing: false,
      meta: { label: "操作", mandatory: true },
      cell: (ctx) => {
        const item = ctx.row.original;
        return (
          <div className="flex items-center gap-0.5">
            <HintTooltip content={canWrite ? "削除" : "リーダー以上の権限が必要です"}>
              {/* 無効なボタンはイベントを出さないため、Tooltip 用に包む。 */}
              <span className="inline-flex">
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  color="danger"
                  aria-label={`${item.sequence} 行目を削除`}
                  isDisabled={!canWrite}
                  onPress={() => onDelete(item)}
                >
                  <Trash2 size={16} />
                </Button>
              </span>
            </HintTooltip>
          </div>
        );
      },
    }),
  ] as ColumnDef<ProjectItem, never>[];
}

/** 列 id の一覧（設定の永続化で「未知のキー」を捨てるのに使う）。 */
export const projectItemColumnIds = [
  "select",
  "sequence",
  "drawing_number",
  "item_name",
  "material",
  "surface_treatment",
  "quantity",
  // 発注書から読み取った単価の列。設定に載っていないと表示列の切り替えが
  // 保存されないので、列定義と同じだけ並べる。
  "client_unit_price",
  "client_unit_no",
  "client_bom_no",
  "client_slip_number",
  "client_order_numbers",
  "deadline",
  "machining_deadline",
  "shipping_planned_date",
  "shipped_date",
  "quotation_item_id",
  "note",
  "actions",
];

/**
 * 既定では非表示にする列。受注時に見積から写してくるが、製作の指示に使うのは
 * 図番・品名・数量・納期なので、常に出すと表が横に伸びて邪魔になる。
 */
export const projectItemDefaultHiddenColumnIds = [
  "material",
  "surface_treatment",
  // 客先の階層と購番。**京セラの紙にしか無い項目**なので、要る人が表示列の設定で
  // 出す（見積明細も同じ扱い）。購番はセット品のときだけ複数本並ぶ。
  "client_unit_no",
  "client_bom_no",
  "client_order_numbers",
  // 伝票番号は**新東工業の紙にしか無い**ので、同じ扱いで既定は隠す。
  "client_slip_number",
];

/** 左に固定する列の id。 */
export const projectItemPinnedColumnIds = ["select", "sequence"];
